// Tenant billing (main-app) — billing-service via the gateway. Tenant scope is
// enforced server-side from the JWT; this never sends a tenantId.
import { gatewayApi } from '@/lib/hrApi';

const B = '/api/v1/billing/tenant';

export interface BillingSummary {
  account: {
    accountStatus: string;
    currency: string;
    billingEmail?: string;
    currentBalance: string;
    tenantNameSnapshot?: string;
    tenantCountry?: string;
  } | null;
  subscription: { billingCycle: string; amount: string; currency: string; nextInvoiceDate?: string } | null;
  plan?: { code: string; name: string; description?: string | null; version?: number; products?: Array<{ productCode: string; limits?: Record<string, unknown> }> } | null;
  addOns?: Array<{ productCode: string; note?: string | null }>;
  currentInvoice: Invoice | null;
  balance: number;
  currency: string;
  suspension: {
    outstandingBalance: number;
    reinstatementFee: number;
    totalDue: number;
    message: string;
  } | null;
}
export interface InvoiceLineItem {
  id: string;
  description: string;
  amount: string;
  unitPrice?: string;
  type?: string;
}
export interface Invoice {
  id: string;
  invoiceNumber: string;
  totalAmount: string;
  amountPaid: string;
  balanceDue: string;
  currency: string;
  status: string;
  dueDate?: string;
  issueDate?: string;
  lineItems?: InvoiceLineItem[];
}
export interface Receipt {
  id: string;
  receiptNumber: string;
  amount: string;
  currency: string;
  issuedAt: string;
}
export interface Payment {
  id: string;
  invoiceId?: string | null;
  provider: string;
  amount: string;
  currency: string;
  status: string;
  method?: string;
  paidAt?: string;
  createdAt: string;
  failureReason?: string | null;
}

export const getSummary = () => gatewayApi.get<BillingSummary>(`${B}/summary`).then((r) => r.data);
export const getInvoices = () => gatewayApi.get<Invoice[]>(`${B}/invoices`).then((r) => r.data);
export const getInvoice = (invoiceId: string) =>
  gatewayApi.get<Invoice>(`${B}/invoices/${invoiceId}`).then((r) => r.data);
// Canonical PDF: returns a short-lived signed download URL (server stores it in Tigris/S3).
export const getInvoicePdfUrl = (invoiceId: string) =>
  gatewayApi.get<{ url?: string; filename?: string }>(`${B}/invoices/${invoiceId}/pdf`).then((r) => r.data);
export const getReceipts = () => gatewayApi.get<Receipt[]>(`${B}/receipts`).then((r) => r.data);
export const getPayments = () => gatewayApi.get<Payment[]>(`${B}/payments`).then((r) => r.data);
export const updateContact = (billingEmail: string) =>
  gatewayApi.put(`${B}/contact`, { billingEmail }).then((r) => r.data);

// Pay-now methods this deployment offers. Exactly two at most: "mpesa" (direct
// Vodacom M-Pesa push into KaziPeople's own account) and "bank" (CRDB / NMB via the
// bank app's one-time code). available=false → show "not available yet", never a
// broken dialog.
export interface PayMethod {
  method: 'mpesa' | 'bank';
  provider: string | null;
  environment?: 'sandbox' | 'live';
  banks?: Array<'CRDB' | 'NMB'>;
}
export interface PaymentOptions {
  available: boolean;
  methods: PayMethod[];
  message?: string;
}
export const getPaymentOptions = () =>
  gatewayApi.get<PaymentOptions>(`${B}/payment-options`).then((r) => r.data);

// One payment attempt — polled while the payer approves the M-Pesa prompt.
// status: pending (prompt on the phone) | success | failed (reason says why) |
// unknown (being confirmed with M-Pesa — the payer must NOT pay again).
export interface PaymentAttempt {
  paymentId: string;
  invoiceId?: string;
  provider: string;
  status: 'initiated' | 'pending' | 'success' | 'failed' | 'unknown' | 'cancelled' | 'expired';
  reason?: string | null;
}
export const getPaymentAttempt = (paymentId: string) =>
  gatewayApi.get<PaymentAttempt>(`${B}/payments/${paymentId}`).then((r) => r.data);

export interface PayResult {
  paymentId: string;
  provider: string;
  // 'redirect' → send the browser to checkoutUrl; 'push' → a prompt was sent to the
  // payer's phone / bank, wait for it to be approved.
  flow?: 'redirect' | 'push';
  status?: string;
  checkoutUrl?: string;
  message?: string;
}
// Pay Now. M-Pesa: phoneNumber + network 'mpesa'. Bank: phoneNumber = the bank
// account number, network 'crdb' | 'nmb', bankOtp = the one-time code from the bank app.
export const payInvoice = (
  invoiceId: string,
  opts?: { phoneNumber?: string; network?: string; bankOtp?: string; provider?: 'AZAMPAY' | 'STRIPE' | 'MANUAL' | 'MPESA' },
) =>
  gatewayApi
    .post<PayResult>(`${B}/invoices/${invoiceId}/pay`, {
      // Deterministic per invoice: a RETRY must reuse the key or the server's
      // unique-key replay protection is defeated (audit Phase 4 — the old
      // Date.now() suffix minted a fresh key per attempt). The server releases the
      // key once an attempt has definitely failed, so a new attempt can follow.
      idempotencyKey: `pay-${invoiceId}`,
      ...opts,
    })
    .then((r) => r.data);
